import React, { useState, useRef, useEffect } from "react";
import "./SignUpForm.css";
import haatzasignup from "../../../assets/Images/haatzasignup1.png";
import Logo from "../../../assets/Images/haatzaSellerlogo.png";
import { Link } from "react-router-dom";
/**
 * SignUpForm — pure UI component
 *
 * Props:
 *   form           {object}  { fullName, phone, email, password }
 *   onFormChange   {fn}      called with (field, value) when any input changes
 *   loading        {bool}    disables inputs & changes button label
 *   error          {string}  error message to display (empty string = none)
 *   success        {string}  success message to display (empty string = none)
 *   onRegister     {fn}      called when "Register" button is clicked / Enter pressed
 *   onNavigateSignIn {fn}    called when "Sign In" link is clicked
 */
function SignUpForm({
  form,
  onFormChange,
  loading,
  error,
  success,
  phoneError,
  emailError,
  onPhoneBlur,
  onEmailBlur,
  onRegister,
  onNavigateSignIn,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const videoRef = useRef(null);

  // ─── Video autoplay (handles mobile browsers) ────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const tryPlay = () => video.play().catch(() => { });
    tryPlay();
    document.addEventListener("touchstart", tryPlay, { once: true });
    document.addEventListener("click", tryPlay, { once: true });
    return () => {
      document.removeEventListener("touchstart", tryPlay);
      document.removeEventListener("click", tryPlay);
    };
  }, []);

  const phoneInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // ─── Enter key field navigation ──────────────────────────────────────────
  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      phoneInputRef.current?.focus();
    }
  };

  const handlePhoneKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      emailInputRef.current?.focus();
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      passwordInputRef.current?.focus();
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onRegister();
    }
  };

  // ─── SVG Icons ───────────────────────────────────────────────────────────
  const EyeOpenIcon = React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    React.createElement("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }),
    React.createElement("circle", { cx: "12", cy: "12", r: "3" })
  );

  const EyeClosedIcon = React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
    },
    React.createElement("path", {
      d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94",
    }),
    React.createElement("path", {
      d: "M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19",
    }),
    React.createElement("line", { x1: "1", y1: "1", x2: "23", y2: "23" })
  );

  // ─── Render ──────────────────────────────────────────────────────────────
  return React.createElement(
    "div",
    { className: "signup-page" },

    // LEFT VIDEO PANEL
    React.createElement(
      "div",
      { className: "illustration-panel desktop-left-illustration" },
      React.createElement("img", {
        className: "side-video desktop-side-image",
        src: haatzasignup,
        alt: "Sign Up Illustration",
      })
    ),

    // RIGHT FORM PANEL
    React.createElement(
      "div",
      { className: "form-panel desktop-right-form-panel" },
      React.createElement(
        "div",
        { className: "form-inner desktop-form-container" },

        // LOGO
        React.createElement(
          "div",
          { className: "logo-wrapper" },
          React.createElement("img", { src: Logo, alt: "Haatza Seller Logo", className: "logo-image" })
        ),

        // TITLE
        React.createElement("h2", { className: "title", style: { color: "#000000", textAlign: "center" } }, "Register"),

        // SUBTITLE
        React.createElement(
          "p",
          { className: "subtitle" },
          "Reach Millions of Customers — Become a Haatza Seller Today"
        ),

        // ERROR BANNER
        error
          ? React.createElement("p", { className: "error-message" }, error)
          : null,

        // SUCCESS BANNER
        success
          ? React.createElement("p", { className: "success-message" }, success)
          : null,

        // FULL NAME
        React.createElement("input", {
          type: "text",
          placeholder: "Full Name",
          className: "input-box",
          value: form.fullName,
          onChange: (e) => onFormChange("fullName", e.target.value),
          onKeyDown: handleNameKeyDown,
          disabled: loading,
        }),

        // PHONE
        React.createElement(
          "div",
          { className: "phone-box" },
          React.createElement("span", { className: "country-code" }, "+91"),
          React.createElement("input", {
            ref: phoneInputRef,
            type: "text",
            placeholder: "Phone Number",
            className: "phone-input",
            value: form.phone,
            maxLength: 10,
            onChange: (e) => onFormChange("phone", e.target.value),
            onBlur: () => onPhoneBlur && onPhoneBlur(),
            onKeyDown: handlePhoneKeyDown,
            disabled: loading,
          })
        ),
        phoneError
          ? React.createElement(
            "p",
            {
              style: {
                color: "#dc2626",
                fontSize: "12.5px",
                margin: "-6px auto 10px auto",
                textAlign: "left",
                paddingLeft: "6px",
                width: "450px",
                maxWidth: "450px",
                boxSizing: "border-box",
              },
            },
            phoneError
          )
          : null,

        // EMAIL
        React.createElement("input", {
          ref: emailInputRef,
          type: "email",
          placeholder: "Email Address",
          className: "input-box",
          value: form.email,
          onChange: (e) => onFormChange("email", e.target.value),
          onBlur: () => onEmailBlur && onEmailBlur(),
          onKeyDown: handleEmailKeyDown,
          disabled: loading,
        }),
        emailError
          ? React.createElement(
            "p",
            {
              style: {
                color: "#dc2626",
                fontSize: "12.5px",
                margin: "-6px auto 10px auto",
                textAlign: "left",
                paddingLeft: "6px",
                width: "450px",
                maxWidth: "450px",
                boxSizing: "border-box",
              },
            },
            emailError
          )
          : null,

        // PASSWORD
        React.createElement(
          "div",
          { className: "password-box" },
          React.createElement("input", {
            ref: passwordInputRef,
            type: showPassword ? "text" : "password",
            placeholder: "Password (min 8 characters)",
            className: "password-input",
            value: form.password,
            onChange: (e) => onFormChange("password", e.target.value),
            onKeyDown: handlePasswordKeyDown,
            disabled: loading,
          }),
          React.createElement(
            "span",
            {
              className: "eye-icon",
              onClick: () => setShowPassword((v) => !v),
            },
            showPassword ? EyeOpenIcon : EyeClosedIcon
          )
        ),

        // TERMS
        React.createElement(
          "p",
          { className: "terms" },
          "By continuing, you agree to our ",
          React.createElement(Link, { to: "/terms", className: "link", style: { margin: "0 4px" } }, "Terms of Use"),
          " and ",
          React.createElement(Link, { to: "/privacy", className: "link", style: { marginLeft: "4px" } }, "Privacy Policy"),
          "."
        ),

        // REGISTER BUTTON
        React.createElement(
          "button",
          {
            className: "btn",
            style: { textAlign: "center", width: "100%", display: "block", margin: "0 auto 20px" },
            onClick: onRegister,
            disabled: loading,
          },
          loading ? "Registering..." : "Register"
        ),

        // SIGN IN LINK
        React.createElement(
          "p",
          { className: "signin-text", style: { display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", textAlign: "center", width: "100%", margin: "16px auto 0" } },
          "Already a Seller? ",
          React.createElement(
            "span",
            { className: "link", onClick: onNavigateSignIn, style: { marginLeft: "4px" } },
            "Sign In"
          )
        )
      )
    )
  );
}

export default SignUpForm;